import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CardIcon from "../../assets/svg/credit-card.svg";
import UPIIcon from "../../assets/svg/UPI.svg";
import WalletIcon from "../../assets/svg/netbanking.svg";
import CODIcon from "../../assets/svg/paylater.svg";
// Assuming these services exist:
import { InitPayment, VerifyPayment, FetchSavedCards, PaymentCOD } from "../../Services/PaymentService";
// Assuming this component exists:
import ErrorPopup from "../StatusPopup/StatusPopup";

const PaymentDetails = ({ selectedAddressId, cartData, order, isCustomisePayment, setIsCustomisePayment }) => {
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [netbankingBanks, setNetbankingBanks] = useState([]);
    const [activeTab, setActiveTab] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorPopup, setErrorPopup] = useState({ show: false, message: "" });
    const [savedCards, setSavedCards] = useState([]);
    const [savedUpi, setSavedUpi] = useState([]);

    const [cardDetails, setCardDetails] = useState({
        name: '', number: '', expiry_month: '', expiry_year: '', cvv: '',
        tokenId: null, last4: '', network: ''
    });
    const [upiDetails, setUpiDetails] = useState({ vpa: '', tokenId: null });

    const [saveCardConsent, setSaveCardConsent] = useState(false);
    const [saveUpiConsent, setSaveUpiConsent] = useState(false);

    const [selectedBank, setSelectedBank] = useState('');
    const [upiFlow, setUpiFlow] = useState('vpa');

    const userId = localStorage.getItem('userId');
    const email = localStorage.getItem('email');
    const phoneNumber = localStorage.getItem('phoneNumber');
    const userName = localStorage.getItem('userName');
    const currentCustomerId = localStorage.getItem('razorpayCustomerId');
    const navigate = useNavigate();
    const location = useLocation();

    let razorpayInstance = null;
    const razorpayKey = (process.env.REACT_APP_RAZORPAY_KEY_ID || "").trim();

    function getRazorpay() {
        if (!razorpayInstance) {
            let options = {
                key: razorpayKey, // Use your actual key
                image: {}
            };
            if (typeof window.Razorpay === 'undefined') { console.error("Razorpay SDK is not loaded."); return null; }
            razorpayInstance = new window.Razorpay(options);
        }
        return razorpayInstance;
    }

    // --- Handlers for Input/Select ---

    // **FIXED LOGIC:** Preserves tokenId if only CVV is being updated for a saved card.
    const handleCardInputChange = (e) => {
        const { id, value } = e.target;

        // Fields that indicate a New Card is being entered
        const isNewCardField = ['name', 'number', 'expiry_month', 'expiry_year'].includes(id);

        setCardDetails(prevDetails => {
            let newTokenId = prevDetails.tokenId;

            // If any new card field is changed, we switch to new card flow (tokenId = null).
            if (isNewCardField) {
                newTokenId = null;
                setSaveCardConsent(false); // Clear consent if switching to new card input
            }

            return {
                ...prevDetails,
                [id]: value,
                tokenId: newTokenId
            };
        });
    };

    const handleSavedCardSelect = (card) => {
        setCardDetails({
            name: card.name,
            number: '',
            expiry_month: '',
            expiry_year: '',
            cvv: '',
            tokenId: card.token,
            last4: card.last4,
            network: card.network
        });
        setSaveCardConsent(false);
    };

    const handleSavedUpiSelect = (token, vpa) => {
        setUpiDetails({ vpa: vpa, tokenId: token });
        setSaveUpiConsent(false);
    };

    const handleUpiInputChange = (e) => {
        setUpiDetails({ vpa: e.target.value, tokenId: null });
        setSaveUpiConsent(false);
    };

    const handleNetbankingSelect = (e) => {
        setSelectedBank(e.target.value);
    };

    // --- Effects and Razorpay Logic ---

    useEffect(() => {
        const runPayment = async () => {
            if (isCustomisePayment && location.pathname === "/customize-wheat") {
                await handlePayNowClick();
                setIsCustomisePayment(false);
            }
        };
        runPayment();
    }, [isCustomisePayment, location.pathname]);

    useEffect(() => {
        const razorpay = getRazorpay();
        if (!razorpay) return;

        const handleReady = () => {
            const methods = razorpay.methods;
            const tabs = [];

            if (methods) {
                if (methods.card) { tabs.push({ id: "card", label: "Card", icon: CardIcon }); }
                if (methods.upi) { tabs.push({ id: "upi", label: "UPI", icon: UPIIcon }); }
                if (methods.netbanking) {
                    tabs.push({ id: "netbanking", label: "Netbanking", icon: WalletIcon });
                    setNetbankingBanks(Object.entries(methods.netbanking).map(([c, n]) => ({ code: c, name: n })));
                }
            }
            tabs.push({ id: "cod", label: "Cash on Delivery", icon: CODIcon });
            setPaymentMethods(tabs);
            if (tabs.length > 0) setActiveTab(tabs[0].id);
        };

        if (razorpay.methods) {
            handleReady();
        } else {
            razorpay.once("ready", handleReady);
        }

        return () => {
            if (razorpay.off) {
                razorpay.off("ready", handleReady);
            }
        };
    }, []);

    useEffect(() => {
        const fetchTokens = async () => {
            const customerId = localStorage.getItem('razorpayCustomerId');
            if (!customerId || customerId === 'undefined' || customerId === 'null' || customerId.trim() === '') {
                return;
            }
            try {
                const response = await FetchSavedCards(customerId);

                if (response.status && response.data?.data.length > 0) {
                    const responseData = response.data.data;

                    const formattedCards = responseData
                        .filter(token => token.method === 'card')
                        .map(token => ({
                            token: token.tokenId,
                            last4: token.card.last4,
                            issuer: token.card.issuer,
                            network: token.card.network,
                            type: token.card.type,
                            name: token.card.name || `${token.card.network} Card`
                        }));

                    const formattedUpi = responseData
                        .filter(token => token.method === 'upi')
                        .map(token => ({
                            token: token.tokenId,
                            vpa: token.upi.vpa,
                            name: "Saved UPI"
                        }));

                    setSavedCards(formattedCards);
                    setSavedUpi(formattedUpi);
                }
            } catch (err) {
                console.error("Failed to fetch saved cards/tokens:", err);
            }
        };
        fetchTokens();
    }, []);

    useEffect(() => {
        // Reset non-active tab states
        setSaveCardConsent(false);
        setSaveUpiConsent(false);

        if (activeTab !== 'card') {
            setCardDetails({ name: '', number: '', expiry_month: '', expiry_year: '', cvv: '', tokenId: null, last4: '', network: '' });
        }
        if (activeTab !== 'upi') {
            setUpiDetails({ vpa: '', tokenId: null });
        }
        if (activeTab !== 'netbanking') {
            setSelectedBank('');
        }
    }, [activeTab]);

    const [showQuickProfileModal, setShowQuickProfileModal] = useState(false);
    const [quickProfile, setQuickProfile] = useState({
        userName: localStorage.getItem('userName') || '',
        email: localStorage.getItem('email') || '',
        phoneNumber: localStorage.getItem('phoneNumber') || ''
    });

    const handleSaveQuickProfile = (e) => {
        e.preventDefault();
        if (!quickProfile.userName?.trim() || !quickProfile.email?.trim() || !quickProfile.phoneNumber?.trim()) {
            setErrorPopup({ show: true, message: "Please fill Name, Email, and Phone Number." });
            return;
        }

        const nameParts = quickProfile.userName.trim().split(" ");
        const fName = nameParts[0] || "";
        const lName = nameParts.slice(1).join(" ") || "";

        localStorage.setItem("userName", quickProfile.userName.trim());
        localStorage.setItem("email", quickProfile.email.trim());
        localStorage.setItem("phoneNumber", quickProfile.phoneNumber.trim());
        localStorage.setItem("firstName", fName);
        localStorage.setItem("lastName", lName);

        setShowQuickProfileModal(false);
        setTimeout(() => {
            handlePayNowClick();
        }, 150);
    };

    const handlePayNowClick = async () => {
        if (!selectedAddressId || !activeTab || loading) { alert("Select address & payment method first."); return; }
        if (!userId) { alert("User not logged in."); return; }

        const currentEmail = localStorage.getItem('email')?.trim() || email?.trim();
        const currentPhone = localStorage.getItem('phoneNumber')?.trim() || phoneNumber?.trim();
        const currentName = localStorage.getItem('userName')?.trim() || userName?.trim();

        if (!currentEmail || !currentPhone || !currentName) {
            setShowQuickProfileModal(true);
            return;
        }

        switch (activeTab) {
            case 'card':
                const isSavedCardSelected = !!cardDetails.tokenId;
                if (isSavedCardSelected) {
                    if (!cardDetails.cvv || cardDetails.cvv.length !== 3) {
                        setErrorPopup({ show: true, message: "Please enter a valid CVV for the saved card." }); return;
                    }
                }
                else {
                    if (!cardDetails.name || !cardDetails.number || !cardDetails.expiry_month || !cardDetails.expiry_year || !cardDetails.cvv) {
                        setErrorPopup({ show: true, message: "Please fill all new card details." }); return;
                    }
                }
                break;
            case 'netbanking': if (!selectedBank) { setErrorPopup({ show: true, message: "Please select a bank." }); return; } break;
            case 'upi':
                const isSavedUpiSelected = !!upiDetails.tokenId;
                const isVpaEntered = !!upiDetails.vpa;
                if (!isSavedUpiSelected && upiFlow === 'vpa' && !isVpaEntered) { setErrorPopup({ show: true, message: "Please enter UPI VPA or select a saved UPI." }); return; }
                break;
            default: break;
        }

        setLoading(true);

        try {
            let gatewayOrderId = order?.razorPayOrderId || "";
            const amount = order?.totalPrice ?? cartData.amount;
            // const amount = 1; // Testing amount

            const razorpay = getRazorpay();
            if (!razorpay) { throw new Error("Razorpay SDK not available."); }

            // 1. Initialize Order if not already done (for cart checkout flow)
            if (order === null) {
                const payload = {
                    amount: parseFloat(amount).toFixed(2),
                    userId: parseInt(userId),
                    addressId: selectedAddressId,
                    couponCode: cartData.couponCode || "",
                    cartItemIds: cartData.cartItemIds
                };

                if (activeTab === 'cod') {
                    // For COD, call the PaymentCOD endpoint with try-catch fallback
                    try {
                        const response = await PaymentCOD(payload);

                        let responseData = response.data;
                        if (responseData && responseData.data) {
                            responseData = responseData.data;
                        }
                        const orderId = responseData?.orderId || responseData?.id;

                        if (orderId) {
                            localStorage.setItem('orderId', orderId);
                            window.dispatchEvent(new Event('cartUpdated'));
                            setLoading(false);
                            navigate("/order-confirmed", { state: { orderId } });
                            return;
                        }

                        if (!response.status) {
                            setErrorPopup({ show: true, message: "Failed to initialize COD order: " + (response.message || "Unknown error") });
                            setLoading(false);
                            return;
                        }
                    } catch (codErr) {
                        console.error("COD submission error:", codErr);
                        setLoading(false);
                        const errMsg = codErr.response?.data?.message || codErr.message || "";
                        
                        // If order was created or cart cleared recently, check stored orderId or navigate safely
                        const storedId = localStorage.getItem('orderId');
                        if (storedId || errMsg.toLowerCase().includes("cart is empty")) {
                            window.dispatchEvent(new Event('cartUpdated'));
                            navigate("/order-confirmed", { state: { orderId: storedId } });
                            return;
                        }

                        setErrorPopup({ show: true, message: errMsg || "Failed to process COD order." });
                        return;
                    }
                } else {
                    // For other payment methods, initialize with InitPayment
                    const response = await InitPayment(payload);

                    if (!response.status || !response.data.data) {
                        setErrorPopup({ show: true, message: "Failed to initialize payment: " + (response.message || "Unknown error") });
                        setLoading(false);
                        return;
                    }

                    if (response.data.message === "Order already paid.") {
                        const responseData = JSON.parse(response.data.data);
                        const orderId = responseData.orderId;
                        localStorage.setItem('orderId', orderId);
                        window.dispatchEvent(new Event('cartUpdated'));
                        navigate("/order-confirmed", { state: { orderId } });
                        setLoading(false);
                        return;
                    }

                    const responseData = JSON.parse(response.data.data);
                    gatewayOrderId = responseData.gatewayOrderId;
                    if (responseData.customerId) {
                        localStorage.setItem('razorpayCustomerId', responseData.customerId);
                    }
                }
            }

            // 2. Prepare Payment Options
            const paymentOptions = {
                amount: amount * 100, currency: "INR", order_id: gatewayOrderId,
                email: email, contact: phoneNumber, notes: { userId: userId }, customer_id: currentCustomerId
            };

            switch (activeTab) {
                case 'card':
                    const isSavingCard = !cardDetails.tokenId;
                    const cardSaveFlag = (isSavingCard && saveCardConsent) ? 1 : 0;

                    if (cardDetails.tokenId) {
                        Object.assign(paymentOptions, { method: 'card', token: cardDetails.tokenId, 'card[cvv]': cardDetails.cvv });
                    } else {
                        Object.assign(paymentOptions, {
                            save: cardSaveFlag, method: 'card',
                            'card[name]': cardDetails.name, 'card[number]': cardDetails.number,
                            'card[cvv]': cardDetails.cvv, 'card[expiry_month]': cardDetails.expiry_month, 'card[expiry_year]': cardDetails.expiry_year.slice(-2)
                        });
                    }
                    break;
                case 'netbanking':
                    Object.assign(paymentOptions, { method: 'netbanking', bank: selectedBank });
                    break;
                case 'upi':
                    const upiSaveFlag = saveUpiConsent ? 1 : 0;

                    if (upiDetails.tokenId) {
                        Object.assign(paymentOptions, { method: 'upi', token: upiDetails.tokenId });
                    } else if (upiFlow === 'vpa') {
                        Object.assign(paymentOptions, { save: upiSaveFlag, method: 'upi', upi: { vpa: upiDetails.vpa, flow: 'collect' } });
                    } else if (upiFlow === 'qr') {
                        Object.assign(paymentOptions, { method: 'upi', upi: { qr: true } });
                    }
                    break;
                case 'cod':
                    // COD handled above, no further action needed
                    break;
                default: break;
            }

            // 3. Initiate Razorpay Payment (only for non-COD methods)
            if (activeTab !== 'cod') {
                razorpay.createPayment(paymentOptions);
            }

            // 4. Handle Payment Success
            razorpay.on('payment.success', async function (resp) {
                var address = (order?.shippingAddressId === selectedAddressId) ? null : selectedAddressId;

                const reqData = {
                    razorpay_payment_id: resp.razorpay_payment_id,
                    razorpay_order_id: resp.razorpay_order_id,
                    razorpay_signature: resp.razorpay_signature,
                    newAddressId: address
                };
                const verify = await VerifyPayment(reqData);

                if (!verify.status) {
                    setErrorPopup({ show: true, message: verify.message });
                    setLoading(false);
                    return;
                }

                const orderId = order ? order?.orderId : verify.data.data.id;
                localStorage.setItem('orderId', orderId);
                window.dispatchEvent(new Event('cartUpdated'));
                navigate("/order-confirmed", { state: { orderId } });
                setLoading(false);
            });

            // 5. Handle Payment Error
            razorpay.on('payment.error', async function (resp) {
                setLoading(false);

                const isIgnorableError = resp.error.description?.toLowerCase().includes("order is already paid") ||
                    resp.error.description?.toLowerCase().includes("cancelled by user");

                if (!isIgnorableError) {
                    setErrorPopup({ show: true, message: resp.error.description || "Payment failed. Please try again." });
                    return;
                }

                const order_id = resp.razorpay_order_id ?? resp.error?.metadata?.order_id;
                const payment_id = resp.razorpay_payment_id ?? resp.error?.metadata?.payment_id;

                if (!(payment_id && order_id)) {
                    setErrorPopup({
                        show: true,
                        message: resp.error.description
                    });
                    return;
                }

                var address = (order?.shippingAddressId === selectedAddressId)
                    ? null
                    : selectedAddressId;
                const reqData = {
                    razorpay_payment_id: payment_id,
                    razorpay_order_id: order_id,
                    razorpay_signature: "",
                    newAddressId: address
                };

                const verify = await VerifyPayment(reqData);

                if (!verify.status) {
                    setErrorPopup({
                        show: true,
                        message: verify.message
                    });
                    return;
                }

                const orderId = order ? order?.orderId : verify.data.data.id;
                localStorage.setItem('orderId', orderId);
                window.dispatchEvent(new Event('cartUpdated'));
                navigate("/order-confirmed", { state: { orderId } });
            });

        } catch (error) {
            console.error("Payment initiation error:", error);
            setErrorPopup({ show: true, message: error.message || "Payment initiation failed." });
            setLoading(false);
        }
    };

    return (
        <div className="payment-details-container">
            <div className="tabs-container">
                <div className="tab-buttons d-flex flex-wrap gap-2">
                    {paymentMethods.map((tab) => (
                        <button key={tab.id}
                            className={`btn btn-outline-secondary ${activeTab === tab.id ? 'active-payment-tab' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                display: 'flex', alignItems: 'center', padding: '10px 15px', borderRadius: '8px',
                                borderColor: activeTab === tab.id ? '#a37c4d' : '#ccc', color: activeTab === tab.id ? '#a37c4d' : '#333',
                                backgroundColor: activeTab === tab.id ? '#fff9f0' : '#fff'
                            }}
                        >
                            <img className={'img-fluid me-2'} src={tab.icon} alt={tab.label} style={{ width: '24px', height: '24px' }} />
                            {tab.label}
                        </button>
                    ))}
                    {paymentMethods.length === 0 && <p>Loading methods...</p>}
                </div>
                <div className="tab-content mt-4 p-3 rounded address-card">
                    <PaymentForm
                        activeTab={activeTab} cardDetails={cardDetails} handleCardInputChange={handleCardInputChange}
                        netbankingBanks={netbankingBanks} selectedBank={selectedBank} handleNetbankingSelect={handleNetbankingSelect}
                        upiFlow={upiFlow} setUpiFlow={setUpiFlow}
                        upiDetails={upiDetails} handleUpiInputChange={handleUpiInputChange}
                        savedCards={savedCards} handleSavedCardSelect={handleSavedCardSelect} paymentMethods={paymentMethods}
                        savedUpi={savedUpi} handleSavedUpiSelect={handleSavedUpiSelect}
                        saveCardConsent={saveCardConsent} setSaveCardConsent={setSaveCardConsent}
                        saveUpiConsent={saveUpiConsent} setSaveUpiConsent={setSaveUpiConsent}
                    />
                </div>
            </div>

            <hr className="mt-4" />
            <div className="d-flex justify-content-end align-items-center">
                {location.pathname === "/customize-wheat" ? "" : (
                    <button
                        className="btn btn-primary" onClick={handlePayNowClick}
                        style={{ backgroundColor: "#a37c4d", borderColor: "#a37c4d", padding: '10px 25px', fontSize: '18px', minWidth: '200px' }}
                        disabled={!selectedAddressId || loading || paymentMethods.length === 0}
                    >
                        {loading ? (
                            <> <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span> Processing... </>
                        ) : (
                            `Pay Now (₹${Number(cartData?.amount || order?.totalPrice || 0).toFixed(2)})`
                        )}
                    </button>
                )}
            </div>
            {!selectedAddressId && (<p className="text-danger mt-3 text-end">⚠️ Please select an address!</p>)}
            <ErrorPopup type="error" show={errorPopup.show} message={errorPopup.message} onClose={() => setErrorPopup({ show: false, message: "" })} />

            {/* Inline Quick Profile Modal */}
            {showQuickProfileModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.65)", zIndex: 1060 }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "480px" }}>
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "16px", overflow: "hidden" }}>
                            <div className="d-flex justify-content-between align-items-center p-4 border-bottom bg-white">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="fas fa-user-circle text-warning fs-5" style={{ color: "#a37c4d" }} />
                                    <h5 className="mb-0 fw-bold text-dark">Contact Information Required</h5>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setShowQuickProfileModal(false)} />
                            </div>
                            <form onSubmit={handleSaveQuickProfile} className="p-4 bg-white">
                                <p className="text-muted small mb-4">
                                    Please enter your contact details to receive order confirmation, tracking updates, and delivery status.
                                </p>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold small text-dark">Full Name *</label>
                                    <input
                                        type="text"
                                        className="form-control py-2"
                                        placeholder="e.g. Rahul Sharma"
                                        value={quickProfile.userName}
                                        onChange={(e) => setQuickProfile({ ...quickProfile, userName: e.target.value })}
                                        required
                                        style={{ borderRadius: "8px", fontSize: "14px" }}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold small text-dark">Email Address *</label>
                                    <input
                                        type="email"
                                        className="form-control py-2"
                                        placeholder="e.g. rahul@gmail.com"
                                        value={quickProfile.email}
                                        onChange={(e) => setQuickProfile({ ...quickProfile, email: e.target.value })}
                                        required
                                        style={{ borderRadius: "8px", fontSize: "14px" }}
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="form-label fw-semibold small text-dark">Phone Number *</label>
                                    <input
                                        type="tel"
                                        className="form-control py-2"
                                        placeholder="e.g. 9876543210"
                                        value={quickProfile.phoneNumber}
                                        onChange={(e) => setQuickProfile({ ...quickProfile, phoneNumber: e.target.value })}
                                        required
                                        style={{ borderRadius: "8px", fontSize: "14px" }}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="btn w-100 py-3 fw-bold text-white shadow-sm"
                                    style={{ backgroundColor: "#a37c4d", borderColor: "#a37c4d", borderRadius: "10px", fontSize: "15px" }}
                                >
                                    Save & Continue to Payment 🚀
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const PaymentForm = ({
    activeTab, paymentMethods, cardDetails, handleCardInputChange, netbankingBanks, selectedBank, handleNetbankingSelect,
    upiFlow, setUpiFlow, upiDetails, handleUpiInputChange, savedCards, handleSavedCardSelect, savedUpi, handleSavedUpiSelect,
    saveCardConsent, setSaveCardConsent, saveUpiConsent, setSaveUpiConsent
}) => {

    const hasSavedCards = savedCards?.length > 0;
    const isSavedCardSelected = !!cardDetails.tokenId;

    // **FIXED INITIALIZATION:** Default to showing the new card form only if no saved cards exist.
    // If saved cards exist, default to showing the saved cards list.
    const [showNewCardForm, setShowNewCardForm] = useState(!hasSavedCards);

    // Effect to control local state based on saved card selection and tab change
    useEffect(() => {
        if (activeTab === 'card') {
            if (isSavedCardSelected) {
                // If a saved card is selected, switch to the CVV input view (hide new card form).
                setShowNewCardForm(false);
            } else if (hasSavedCards) {
                // If not selected, but saved cards exist, ensure saved card view is visible.
                setShowNewCardForm(false);
            } else {
                // If no saved cards exist, show new card form.
                setShowNewCardForm(true);
            }
        }
    }, [isSavedCardSelected, hasSavedCards, activeTab]);

    if (paymentMethods.length === 0) {
        return <div className="text-center py-4">Loading payment methods...</div>;
    }

    switch (activeTab) {
        case 'card': {

            // Render Saved Cards list view
            if (hasSavedCards && !showNewCardForm) {
                return (
                    <div className="card-payment-form">

                        <h5 className="fs-16 fw-600 mb-3">Saved Cards</h5>

                        {/* Saved Cards - 4 per row */}
                        <div className="row g-4">
                            {savedCards.map(card => {
                                const isSelected = cardDetails.tokenId === card.token;

                                return (
                                    <div key={card.token} className="col-12 col-sm-6 col-md-4 col-lg-3">

                                        {/* CARD BOX */}
                                        <label
                                            className={`address-card ${isSelected ? "border-primary-light" : ""}`}
                                            style={{ cursor: "pointer", padding: "15px", border: isSelected ? '2px solid #a37c4d' : '1px solid #ccc' }}
                                            onClick={() => {
                                                // Only select if not already selected to allow user to focus on CVV
                                                if (cardDetails.tokenId !== card.token) {
                                                    handleSavedCardSelect(card);
                                                }
                                            }}
                                        >
                                            <div className="d-flex flex-column gap-1">
                                                <div className="form-check custom-radio d-flex align-items-start gap-2">
                                                    {/* RADIO BUTTON */}
                                                    <input
                                                        type="radio"
                                                        name="savedCard"
                                                        className="form-check-input mt-1"
                                                        checked={isSelected}
                                                        readOnly
                                                        style={{ accentColor: '#a37c4d' }}
                                                    />
                                                    <div>
                                                        <div className="fw-600 text-truncate" title={card.name}>
                                                            {card.issuer || card.network}
                                                        </div>
                                                        <small className="text-muted" style={{ paddingTop: "2px" }}>
                                                            **** **** **** {card.last4}
                                                        </small>
                                                    </div>
                                                </div>

                                                {/* CVV BOX ONLY IF SELECTED */}
                                                {isSelected && (
                                                    <div className="mt-3">
                                                        <label className="form-label fw-500 mb-1 small">Enter CVV</label>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            id="cvv"
                                                            placeholder="123"
                                                            maxLength="3"
                                                            value={cardDetails.cvv}
                                                            onChange={handleCardInputChange}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Add New Card Button */}
                        <div className="mt-4 pt-2 border-top">
                            <button
                                className="btn btn-dark w-auto" style={{ backgroundColor: "#a37c4d", borderColor: "#a37c4d" }}
                                onClick={() => {
                                    setShowNewCardForm(true);
                                    // Clear saved card selection when switching to new card entry
                                    handleSavedCardSelect({ token: null, name: cardDetails.name, last4: '', network: '' });
                                }}
                            >
                                Add New Card
                            </button>
                        </div>
                    </div>
                )
            }

            // Render New Card form view
            return (
                <div className="card-payment-form">

                    {hasSavedCards && (
                        <button
                            className="btn btn-secondary mb-3 btn-sm"
                            style={{ backgroundColor: "#a37c4d", borderColor: "#a37c4d" }}
                            onClick={() => {
                                setShowNewCardForm(false);
                                // Clear new card details when going back to saved cards
                                handleSavedCardSelect({ token: null, name: '', last4: '', network: '' });
                            }}
                        >
                            ← Back to Saved Cards
                        </button>
                    )}

                    <h5 className="fs-16 fw-600 mb-3 border-bottom pb-2">Enter New Card Details</h5>

                    <div className="row g-4">

                        {/* NAME */}
                        <div className="col-md-6">
                            <label className="form-label">Cardholder's Name</label>
                            <input
                                type="text"
                                id="name"
                                className="form-control"
                                placeholder="John Doe"
                                value={cardDetails.name}
                                onChange={handleCardInputChange}
                            />
                        </div>

                        {/* NUMBER */}
                        <div className="col-md-6">
                            <label className="form-label">Card Number</label>
                            <input
                                type="text"
                                id="number"
                                className="form-control"
                                placeholder="4XXX XXXX XXXX XXXX"
                                value={cardDetails.number}
                                onChange={handleCardInputChange}
                            />
                        </div>

                        {/* EXP MONTH */}
                        <div className="col-md-3">
                            <label className="form-label">Expiry Month</label>
                            <input
                                type="text"
                                id="expiry_month"
                                className="form-control"
                                placeholder="MM"
                                maxLength="2"
                                value={cardDetails.expiry_month}
                                onChange={handleCardInputChange}
                            />
                        </div>

                        {/* EXP YEAR */}
                        <div className="col-md-3">
                            <label className="form-label">Expiry Year</label>
                            <input
                                type="text"
                                id="expiry_year"
                                className="form-control"
                                placeholder="YYYY"
                                maxLength="4"
                                value={cardDetails.expiry_year}
                                onChange={handleCardInputChange}
                            />
                        </div>

                        {/* CVV */}
                        <div className="col-md-6">
                            <label className="form-label">CVV</label>
                            <input
                                type="text"
                                id="cvv"
                                className="form-control"
                                placeholder="123"
                                maxLength="3"
                                value={cardDetails.cvv}
                                onChange={handleCardInputChange}
                            />
                        </div>
                    </div>

                    {/* SAVE CARD CHECKBOX */}
                    <div className="form-check mt-3">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="saveCardConsent"
                            checked={saveCardConsent}
                            onChange={(e) => setSaveCardConsent(e.target.checked)}
                            style={{ accentColor: '#a37c4d' }}
                        />
                        <label className="form-check-label fw-500" htmlFor="saveCardConsent">
                            Save this card securely for future payments
                        </label>
                    </div>
                </div>
            );
        }

        case 'netbanking':
            return (
                <div>
                    <div className={'form-input w-100 w-md-50'}>
                        <label htmlFor="bankSelect" className="form-label">Select Bank</label>
                        <select id="bankSelect" className="form-control" onChange={handleNetbankingSelect} value={selectedBank} required>
                            <option value="">--- Select Bank ---</option>
                            {netbankingBanks.map((bank) => (<option key={bank.code} value={bank.code}>{bank.name}</option>))}
                        </select>
                    </div>
                </div>
            );

        case 'upi':
            const isSavedUpiSelected = !!upiDetails.tokenId;
            const selectedUpi = savedUpi.find(u => u.token === upiDetails.tokenId) || {};

            return (
                <div className="upi-options">

                    {/* 1. SAVED UPI LIST (Visible if tokens exist) */}
                    {savedUpi && savedUpi.length > 0 && (
                        <div className="mb-4 p-3 border rounded" style={{ backgroundColor: '#f9f9f9', border: '1px solid #a37c4d' }}>
                            <h5 className="fs-16 fw-600 mb-3 text-dark">📱 Saved UPI VPA(s) ({savedUpi.length}):</h5>
                            <div className="d-flex flex-column gap-2">
                                {savedUpi.map(upi => (
                                    <label key={upi.token} className={`form-check d-flex align-items-center gap-2 p-2 rounded ${upiDetails.tokenId === upi.token ? 'bg-light-success border-success' : 'border'}`} style={{ backgroundColor: upiDetails.tokenId === upi.token ? '#fff9f0' : '#fff' }}>
                                        <input
                                            className="form-check-input" type="radio" name="savedUpiRadio"
                                            checked={upiDetails.tokenId === upi.token}
                                            onChange={() => { handleSavedUpiSelect(upi.token, upi.vpa); setUpiFlow('vpa'); }}
                                            style={{ accentColor: '#a37c4d' }}
                                        />
                                        <span className="fw-500">{upi.vpa}</span>
                                    </label>
                                ))}
                            </div>

                            {/* Toggle Button to use new VPA/QR */}
                            {isSavedUpiSelected && (
                                <button className="btn btn-sm btn-link mt-3 p-0 text-muted" onClick={() => handleSavedUpiSelect(null, '')}>
                                    Use a new VPA/QR
                                </button>
                            )}
                            <hr className="mt-4 mb-3" />
                        </div>
                    )}

                    {/* 2. VPA / QR Flow Toggle */}
                    <h5 className="fs-16 fw-600 mb-3 border-bottom pb-2">Select Payment Method</h5>
                    <div className="d-flex gap-4 mb-3">
                        <div className="form-check custom-radio">
                            <input className="form-check-input" type="radio" name="upiFlowRadio" id="upiVpaFlow" value="vpa" checked={upiFlow === 'vpa'} onChange={() => { setUpiFlow('vpa'); if (!isSavedUpiSelected) handleSavedUpiSelect(null, ''); }} style={{ accentColor: '#a37c4d' }} />
                            <label className="form-check-label fw-500" htmlFor="upiVpaFlow">Pay via UPI ID (VPA Collect)</label>
                        </div>
                        <div className="form-check custom-radio">
                            <input className="form-check-input" type="radio" name="upiFlowRadio" id="upiQrFlow" value="qr" checked={upiFlow === 'qr'} onChange={() => { setUpiFlow('qr'); handleSavedUpiSelect(null, ''); }} style={{ accentColor: '#a37c4d' }} />
                            <label className="form-check-label fw-500" htmlFor="upiQrFlow">Scan & Pay (QR Code)</label>
                        </div>
                    </div>

                    {/* 3. VPA INPUT FIELD */}
                    {upiFlow === 'vpa' && (
                        <div className={'form-input mt-3 w-100 w-md-50'}>
                            {isSavedUpiSelected ? (
                                <div className="alert alert-success p-2 small">
                                    Using Saved VPA: {selectedUpi.vpa}
                                </div>
                            ) : (
                                <>
                                    <label htmlFor="vpa" className="form-label">UPI VPA (Virtual Payment Address)</label>
                                    <input type="email" className="form-control" id="vpa"
                                        placeholder="name@bank"
                                        value={upiDetails.vpa}
                                        onChange={handleUpiInputChange}
                                        required={true}
                                    />
                                    {/* SAVE UPI CHECKBOX */}
                                    <div className="form-check custom-checkbox mt-2">
                                        <input className="form-check-input" type="checkbox" id="saveUpiConsent"
                                            checked={saveUpiConsent}
                                            onChange={(e) => setSaveUpiConsent(e.target.checked)}
                                            style={{ accentColor: '#a37c4d' }}
                                        />
                                        <label className="form-check-label fw-500 small" htmlFor="saveUpiConsent">
                                            Save this UPI VPA for future use.
                                        </label>
                                    </div>

                                    {/* Toggle Button to use saved UPI if user is filling new VPA form */}
                                    {savedUpi?.length > 0 && (
                                        <div className="mt-3">
                                            <button
                                                className="btn btn-sm btn-link p-0 text-success"
                                                onClick={() => handleSavedUpiSelect(savedUpi[0].token, savedUpi[0].vpa)}
                                            >
                                                Use a Saved UPI VPA instead
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {upiFlow === 'qr' && (
                        <div className="alert alert-info mt-3">Scan &amp; Pay selected: After clicking 'Pay Now', a QR code will be displayed for you to scan and complete the payment using any UPI app.</div>
                    )}
                </div>
            );

        case 'cod':
            return (
                <div className="cod-options">
                    <h5 className="fs-16 fw-600 mb-3">Cash on Delivery</h5>
                    <div className="alert alert-warning">
                        You will pay in cash upon delivery of your order. No online payment required at this step.
                    </div>
                </div>
            );

        default:
            return <div>Please select a payment method.</div>;
    }
};

export default PaymentDetails;